import { lazy, Suspense, type ReactNode } from 'react';
import { createBrowserRouter, Navigate, useParams } from 'react-router';
import { Shell } from './components/shell';
import Discover from './routes/discover';
import Story from './routes/story';

const Category = lazy(() => import('./routes/category'));
const Source = lazy(() => import('./routes/source'));
const Today = lazy(() => import('./routes/today'));
const Library = lazy(() => import('./routes/library'));
const Sources = lazy(() => import('./routes/sources'));
const Markets = lazy(() => import('./routes/markets'));
const SymbolRoute = lazy(() => import('./routes/symbol'));
const Settings = lazy(() => import('./routes/settings'));
const Interests = lazy(() => import('./routes/interests'));
const SignIn = lazy(() => import('./routes/signin'));
const AuthCallback = lazy(() => import('./routes/auth-callback'));
const Onboarding = lazy(() => import('./routes/onboarding'));
const NotFound = lazy(() => import('./routes/not-found'));
const Pricing = lazy(() => import('./routes/pricing'));
const Checkout = lazy(() => import('./routes/checkout'));
const CheckoutSuccess = lazy(() => import('./routes/checkout').then((m) => ({ default: m.CheckoutSuccess })));
const Legal = lazy(() => import('./routes/legal'));
const AgentClaim = lazy(() => import('./routes/agent-claim'));

/** Parametre değişince (/s/a → /s/b) sayfa durumunu sıfırlamak için anahtarla. */
function Keyed({ param, children }: { param: string; children: (key: string) => ReactNode }) {
  const p = useParams();
  return <>{children(p[param] ?? '')}</>;
}

const s = (el: ReactNode) => <Suspense fallback={<div className="page" style={{ minHeight: '60vh' }} />}>{el}</Suspense>;

export const router = createBrowserRouter([
  { path: '/signin', element: s(<SignIn />) },
  { path: '/auth/callback', element: s(<AuthCallback />) },
  { path: '/welcome', element: s(<Onboarding />) },
  {
    element: <Shell />,
    children: [
      { path: '/', element: <Discover /> },
      { path: '/s/:id', element: <Keyed param="id">{(k) => <Story key={k} />}</Keyed> },
      { path: '/c/:slug', element: s(<Keyed param="slug">{(k) => <Category key={k} />}</Keyed>) },
      { path: '/source/:id', element: s(<Keyed param="id">{(k) => <Source key={k} />}</Keyed>) },
      { path: '/today', element: s(<Today />) },
      { path: '/library', element: s(<Library />) },
      { path: '/library/:tab', element: s(<Library />) },
      { path: '/sources', element: s(<Sources />) },
      { path: '/markets', element: s(<Markets />) },
      { path: '/markets/:symbol', element: s(<Keyed param="symbol">{(k) => <SymbolRoute key={k} />}</Keyed>) },
      { path: '/settings', element: s(<Settings />) },
      { path: '/settings/interests', element: s(<Interests />) },
      { path: '/pricing', element: s(<Pricing />) },
      { path: '/checkout', element: s(<Checkout />) },
      { path: '/checkout/success', element: s(<CheckoutSuccess />) },
      { path: '/agents/claim', element: s(<AgentClaim />) },
      { path: '/legal/:doc', element: s(<Keyed param="doc">{(k) => <Legal key={k} />}</Keyed>) },
      { path: '/terms', element: <Navigate to="/legal/terms" replace /> },
      { path: '/privacy', element: <Navigate to="/legal/privacy" replace /> },
      { path: '/refund', element: <Navigate to="/legal/refund" replace /> },
      { path: '*', element: s(<NotFound />) },
    ],
  },
]);
